// Product Management page of the Purchase panel (Products in its sub-nav), also
// shown on its own at /shop/admin/products. Same look as VendorsView (it reuses
// its vv-* layout styles): header, one filter bar, product table, pagination.
// The Add / Edit Product form and the delete confirmation are the shop ones.
import React, { useCallback, useEffect, useState } from 'react';
import {
    Plus,
    Search,
    Pencil,
    MoreHorizontal,
    Package,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { api } from '../../../api';
import showToast from '../../../utils/toast';
import ProductFormModal from '../../shop/ProductFormModal';
import DeleteConfirmModal from '../../shop/DeleteConfirmModal';
import './VendorsView.css';
import './ProductsView.css';

const PRODUCT_CATEGORIES = ['ISF Shop', 'Medicines', 'Consumables', 'Repairs', 'Infra', 'Others'];
const PAGE_SIZE = 10;
const NOT_ENTERED = '—';

const errorMessageOf = (err, fallback) => {
    const apiErrors = err.response?.data?.errors;
    if (Array.isArray(apiErrors) && apiErrors.length > 0) return apiErrors[0].message;
    return err.response?.data?.message || err.message || fallback;
};

const imageOf = (product) =>
    product.imageUrl ||
    product.primaryImageUrl ||
    product.images?.find((img) => img.isPrimary)?.url ||
    product.images?.[0]?.url ||
    '';

const hasValue = (value) => value !== undefined && value !== null && value !== '';

// Only ISF Shop products are shown to students, so only they can be switched
// on; a product that isn't set up yet (Pending) can only be switched off.
const canToggle = (product) =>
    product.category === 'ISF Shop' && (product.isActive || !product.isPendingProduct);

const getPageButtons = (current, pageCount) => {
    const start = Math.max(1, Math.min(current - 2, pageCount - 4));
    const end = Math.min(pageCount, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export default function ProductsView() {
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [vendorNames, setVendorNames] = useState({});
    const [formProduct, setFormProduct] = useState(null); // { product }; product null = Add Product
    const [deletingProduct, setDeletingProduct] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    const [menuProductId, setMenuProductId] = useState(null);
    const [brokenImages, setBrokenImages] = useState({});

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: PAGE_SIZE, sortBy: 'createdAt', sortOrder: 'desc' };
            if (search.trim()) params.search = search.trim();
            if (categoryFilter !== 'all') params.category = categoryFilter;
            if (statusFilter !== 'all') params.isActive = statusFilter === 'active' ? 'true' : 'false';

            const response = await api.get('/api/v2/shop/admin/products', { params });
            setProducts(response.data.products || []);
            setTotal(response.data.pagination?.total || 0);
        } catch (err) {
            const message = errorMessageOf(err, 'Failed to load products');
            setError(message);
            setProducts([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [page, search, categoryFilter, statusFilter]);

    useEffect(() => {
        const timer = setTimeout(fetchProducts, search ? 300 : 0);
        return () => clearTimeout(timer);
    }, [fetchProducts, search]);

    // Products keep only their approved vendors' ids; the names come from the
    // vendor list (the API caps a page at 100 vendors).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await api.get('/api/v2/vendors', { params: { limit: 100 } });
                if (cancelled) return;
                const names = {};
                (response.data?.vendors || []).forEach((vendor) => {
                    names[vendor._id] = vendor.name;
                });
                setVendorNames(names);
            } catch (err) {
                // Without names every rank shows "Not set".
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Close the row menu on any click outside it.
    useEffect(() => {
        if (!menuProductId) return undefined;
        const close = () => setMenuProductId(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [menuProductId]);

    const changeFilter = (setter) => (e) => {
        setter(e.target.value);
        setPage(1);
    };

    const handleFormSubmit = async (formData) => {
        const editing = formProduct?.product?._id;
        try {
            const response = editing
                ? await api.put(`/api/v2/shop/admin/products/${editing}`, formData)
                : await api.post('/api/v2/shop/admin/products', formData);
            const saved = response.data?.product;

            // The form's Status switch: create / update decide visibility from
            // the category, so a different choice goes through Make Active / Inactive.
            if (saved?._id && saved.category === 'ISF Shop' && Boolean(saved.isActive) !== Boolean(formData.isActive)) {
                try {
                    await api.post(`/api/v2/shop/admin/products/${saved._id}/${formData.isActive ? 'restore' : 'deactivate'}`);
                } catch (statusErr) {
                    showToast(errorMessageOf(statusErr, 'Product saved, but its status could not be changed'), 'error');
                }
            }

            showToast(editing ? 'Product updated successfully' : 'Product created successfully', 'success');
            setFormProduct(null);
            fetchProducts();
        } catch (err) {
            showToast(errorMessageOf(err, 'Failed to save product'), 'error');
            throw err; // the form keeps itself open
        }
    };

    const handleToggleActive = async (product) => {
        setTogglingId(product._id);
        try {
            const action = product.isActive ? 'deactivate' : 'restore';
            await api.post(`/api/v2/shop/admin/products/${product._id}/${action}`);
            showToast(product.isActive ? 'Product is now inactive' : 'Product is now active', 'success');
            fetchProducts();
        } catch (err) {
            showToast(errorMessageOf(err, 'Failed to change product status'), 'error');
        } finally {
            setTogglingId(null);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            await api.delete(`/api/v2/shop/admin/products/${deletingProduct._id}`);
            showToast('Product deleted successfully', 'success');
            setDeletingProduct(null);
            fetchProducts();
        } catch (err) {
            showToast(errorMessageOf(err, 'Failed to delete product'), 'error');
        }
    };

    // Rank 1 and Rank 2 always show; a third rank only when one is set.
    const vendorRanks = (product) => {
        const byRank = {};
        (product.approvedVendors || []).forEach((entry) => {
            const id = entry.vendorId?._id || entry.vendorId;
            byRank[entry.rank || 1] = vendorNames[id] || entry.vendorId?.name || null;
        });
        const ranks = byRank[3] ? [1, 2, 3] : [1, 2];
        return ranks.map((rank) => ({ rank, name: byRank[rank] }));
    };

    const toggleTitle = (product) => {
        if (product.category !== 'ISF Shop') return 'Only ISF Shop products are shown to students';
        if (!canToggle(product)) return 'Finish setting up this product before making it active';
        return product.isActive ? 'Hide this product from students (it is not deleted)' : 'Show this product to students again';
    };

    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const firstIndex = (page - 1) * PAGE_SIZE;
    const columnCount = 8;

    return (
        <div className="vv-root">
            <div className="vv-header">
                <div>
                    <p className="vv-breadcrumb">
                        Purchase <span aria-hidden="true">›</span> ISF Shop <span aria-hidden="true">›</span>{' '}
                        <strong>Products</strong>
                    </p>
                    <h1>Product Management</h1>
                    <p className="vv-subtitle">Manage shop products and Inventory</p>
                </div>
                <button type="button" className="vv-add-btn" onClick={() => setFormProduct({ product: null })}>
                    <Plus size={20} /> Add Product
                </button>
            </div>

            <div className="vv-panel pv-panel">
                <div className="pv-toolbar">
                    <div className="vv-search pv-search">
                        <Search size={18} className="vv-search-icon" aria-hidden="true" />
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={search}
                            onChange={changeFilter(setSearch)}
                            aria-label="Search products"
                        />
                    </div>
                    <select
                        className="pv-select"
                        value={categoryFilter}
                        onChange={changeFilter(setCategoryFilter)}
                        aria-label="Category"
                    >
                        <option value="all">All categories</option>
                        {PRODUCT_CATEGORIES.map((category) => (
                            <option key={category} value={category}>{category}</option>
                        ))}
                    </select>
                    <select
                        className="pv-select"
                        value={statusFilter}
                        onChange={changeFilter(setStatusFilter)}
                        aria-label="Status"
                    >
                        <option value="all">All statuses</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>

                <h2 className="pv-count">Products ({total})</h2>

                <div className="vv-table-wrapper">
                    <table className="vv-table pv-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Purchase<br />rate (₹)</th>
                                <th>Selling rate<br />(ISF Coins)</th>
                                <th>Stock<br />quantity</th>
                                <th>Min. stock<br />quantity</th>
                                <th>Approved vendors</th>
                                <th>Status</th>
                                <th className="pv-actions-head">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={columnCount} className="vv-empty">Loading products...</td></tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan={columnCount} className="vv-empty">
                                        {error}{' '}
                                        <button type="button" className="pv-retry" onClick={fetchProducts}>Try again</button>
                                    </td>
                                </tr>
                            ) : products.length === 0 ? (
                                <tr><td colSpan={columnCount} className="vv-empty">No products found</td></tr>
                            ) : (
                                products.map((product) => {
                                    const image = imageOf(product);
                                    const sellingRate = product.discountPrice ?? product.price;
                                    return (
                                        <tr key={product._id}>
                                            <td>
                                                <span className="pv-product">
                                                    {image && !brokenImages[product._id] ? (
                                                        <img
                                                            className="pv-product-img"
                                                            src={image}
                                                            alt=""
                                                            onError={() => setBrokenImages((prev) => ({ ...prev, [product._id]: true }))}
                                                        />
                                                    ) : (
                                                        <span className="pv-product-img pv-product-placeholder" aria-hidden="true">
                                                            <Package size={24} />
                                                        </span>
                                                    )}
                                                    <span className="pv-product-text">
                                                        <span className="pv-product-name">{product.name}</span>
                                                        <span className="pv-tags">
                                                            <span className="pv-category">{product.category}</span>
                                                            {product.category === 'ISF Shop' && product.isEssential === false && (
                                                                <span className="pv-not-essential" title="Shown in the shop, but children can't buy it">
                                                                    Not essential
                                                                </span>
                                                            )}
                                                        </span>
                                                    </span>
                                                </span>
                                            </td>
                                            <td className="pv-center">
                                                {hasValue(product.maxPrice) ? `₹${product.maxPrice}` : NOT_ENTERED}
                                            </td>
                                            <td>
                                                {hasValue(sellingRate) ? (
                                                    <span className="pv-coins">
                                                        <span className="pv-coin" aria-hidden="true">₹</span>
                                                        <strong>{sellingRate}</strong>
                                                        {hasValue(product.discountPrice) && (
                                                            <s className="pv-was">{product.price}</s>
                                                        )}
                                                    </span>
                                                ) : NOT_ENTERED}
                                            </td>
                                            <td className="pv-center">
                                                {hasValue(product.stock) ? product.stock : NOT_ENTERED}
                                                {product.stock === 0 && <span className="pv-stock-note pv-out">Out of stock</span>}
                                                {product.stock > 0 && product.stock <= product.lowStockThreshold && (
                                                    <span className="pv-stock-note pv-low">Low stock</span>
                                                )}
                                            </td>
                                            <td className="pv-center">
                                                {hasValue(product.lowStockThreshold) ? product.lowStockThreshold : NOT_ENTERED}
                                            </td>
                                            <td>
                                                <span className="pv-ranks">
                                                    {vendorRanks(product).map(({ rank, name }) => (
                                                        <span key={rank}>
                                                            <strong>Rank {rank}:</strong>{' '}
                                                            {name ? <span className="pv-vendor">{name}</span> : <span className="pv-not-set">Not set</span>}
                                                        </span>
                                                    ))}
                                                </span>
                                            </td>
                                            <td>
                                                <label
                                                    className={`vv-switch pv-switch ${canToggle(product) ? '' : 'vv-switch-locked'}`}
                                                    title={toggleTitle(product)}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(product.isActive)}
                                                        onChange={() => handleToggleActive(product)}
                                                        disabled={!canToggle(product) || togglingId === product._id}
                                                        aria-label={product.isActive ? 'Make product inactive' : 'Make product active'}
                                                    />
                                                    <span className="vv-switch-track" aria-hidden="true" />
                                                    <span className={product.isActive ? 'pv-status-active' : 'pv-status-inactive'}>
                                                        {product.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </label>
                                                {product.isPendingProduct && <span className="pv-pending">Pending setup</span>}
                                            </td>
                                            <td>
                                                <span className="pv-actions">
                                                    <button
                                                        type="button"
                                                        className="pv-action-btn"
                                                        title="Edit product"
                                                        aria-label="Edit product"
                                                        onClick={() => setFormProduct({ product })}
                                                    >
                                                        <Pencil size={18} />
                                                    </button>
                                                    <span className="vv-menu-anchor">
                                                        <button
                                                            type="button"
                                                            className="pv-action-btn"
                                                            title="More actions"
                                                            aria-label="More actions"
                                                            aria-haspopup="menu"
                                                            aria-expanded={menuProductId === product._id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setMenuProductId(menuProductId === product._id ? null : product._id);
                                                            }}
                                                        >
                                                            <MoreHorizontal size={20} />
                                                        </button>
                                                        {menuProductId === product._id && (
                                                            <div className="vv-menu pv-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    type="button"
                                                                    role="menuitem"
                                                                    className="pv-menu-danger"
                                                                    onClick={() => {
                                                                        setMenuProductId(null);
                                                                        setDeletingProduct(product);
                                                                    }}
                                                                >
                                                                    Delete product
                                                                </button>
                                                            </div>
                                                        )}
                                                    </span>
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="vv-pagination pv-pagination">
                    <span className="vv-pagination-info">
                        {total > 0
                            ? `Showing ${firstIndex + 1}–${Math.min(firstIndex + PAGE_SIZE, total)} of ${total} products`
                            : 'Showing 0 products'}
                        <span className="pv-legend">{NOT_ENTERED} Not entered</span>
                    </span>
                    {total > 0 && (
                        <div className="vv-pagination-controls pv-pagination-controls">
                            <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} aria-label="Previous page">
                                <ChevronLeft size={18} />
                            </button>
                            {getPageButtons(page, pageCount).map((num) => (
                                <button
                                    key={num}
                                    type="button"
                                    className={num === page ? 'active' : ''}
                                    onClick={() => setPage(num)}
                                    aria-current={num === page ? 'page' : undefined}
                                >
                                    {num}
                                </button>
                            ))}
                            <button type="button" disabled={page >= pageCount} onClick={() => setPage(page + 1)} aria-label="Next page">
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {formProduct && (
                <ProductFormModal
                    product={formProduct.product}
                    onClose={() => setFormProduct(null)}
                    onSubmit={handleFormSubmit}
                    onRefresh={fetchProducts}
                />
            )}

            {deletingProduct && (
                <DeleteConfirmModal
                    product={deletingProduct}
                    onClose={() => setDeletingProduct(null)}
                    onConfirm={handleConfirmDelete}
                />
            )}
        </div>
    );
}
